import { z } from 'zod'
import { type EventIndex, eventIndexSchema } from '../types'
import type { SubscanClient } from './client'

type GetEventsParameters = {
  client: SubscanClient
  address: string
  page?: number
  fromBlock?: number
  toBlock?: number
  headers?: Record<string, string>
}

/** Subscan events API の 1 request あたりの取得件数 */
const subscanEventsRow = 25

const resultSchema = z.object({
  count: z.number().int(),
  events: z
    .array(eventIndexSchema)
    .nullable()
    .transform((value) => value ?? []),
})

export type GetEventsReturnTypes = {
  count: number
  events: readonly EventIndex[]
}

/**
 * Subscan events API に渡す block range が有効か検証する。
 * @param params - 検証対象の開始 block と終了 block
 * @throws block range が Subscan に渡せない値の場合
 */
const validateBlockRange = (params: {
  fromBlock: number
  toBlock: number
}): void => {
  const { fromBlock, toBlock } = params
  if (
    !Number.isSafeInteger(fromBlock) ||
    !Number.isSafeInteger(toBlock) ||
    fromBlock < 0 ||
    toBlock < 0 ||
    fromBlock > toBlock
  ) {
    throw new Error(
      `Invalid Subscan block_range: fromBlock=${fromBlock}, toBlock=${toBlock}`,
    )
  }
}

/**
 * Subscan events API から address に紐づく event 一覧を取得する。
 * @param params - Subscan client と検索条件
 * @returns event 件数と event index 一覧
 */
export const getEvents = async ({
  client,
  address,
  page = 0,
  fromBlock,
  toBlock,
  headers,
}: GetEventsParameters): Promise<GetEventsReturnTypes> => {
  if (fromBlock !== undefined && toBlock !== undefined) {
    validateBlockRange({ fromBlock, toBlock })
  }

  const body = {
    address,
    row: subscanEventsRow,
    page,
    order: 'asc',
    ...(fromBlock !== undefined && toBlock !== undefined
      ? { block_range: `${fromBlock}-${toBlock}` }
      : {}),
  }

  const result = await client.post({
    path: '/api/v2/scan/events',
    body: JSON.stringify(body),
    resultSchema,
    headers,
  })
  return result
}
